import { z } from 'zod';
import { Classification } from './classification.js';

/**
 * LLM runtime configuration (ADR-017, ADR-028). Non-secret; provided as JSON configuration.
 * Model ids appear ONLY here (configuration), never in code or workflow definitions.
 * API keys are vault credentials referenced by name.
 */
export const MODEL_PROFILES = [
  'reasoning',
  'extraction',
  'classification',
  'summarization',
  'validation',
  'cheap-fast',
] as const;
export const ModelProfile = z.enum(MODEL_PROFILES);
export type ModelProfile = z.infer<typeof ModelProfile>;

export const ModelPricing = z.object({
  inputPerMTok: z.number().nonnegative(),
  outputPerMTok: z.number().nonnegative(),
});

export const LlmProviderConfig = z.object({
  /** Adapter kind, e.g. "anthropic", "openai-compatible". */
  kind: z.string().regex(/^[a-z0-9][a-z0-9-]*$/),
  /** Name of the vault credential holding the API key (per organization). */
  credential: z
    .string()
    .regex(/^[a-z0-9][a-z0-9._-]{1,62}$/)
    .optional(),
  /** Adapter-specific, non-secret settings (base URL, API version...). */
  settings: z.record(z.string(), z.unknown()).default({}),
  /**
   * Classifications this provider may receive. External providers never receive RESTRICTED;
   * an empty list means "nothing" (deny until configured).
   */
  eligibleClassifications: z.array(Classification).default(['PUBLIC']),
  /** Price per million tokens for every model used through this provider (budget enforcement). */
  pricing: z.record(z.string(), ModelPricing).default({}),
});
export type LlmProviderConfig = z.infer<typeof LlmProviderConfig>;

export const LlmProfileConfig = z.object({
  provider: z.string(),
  model: z.string().min(1),
  maxOutputTokens: z.number().int().positive().max(64_000).default(4096),
  temperature: z.number().min(0).max(2).optional(),
  /** Per-call timeout for this profile (long structured drafts take minutes); gateway default otherwise. */
  timeoutMs: z.number().int().min(10_000).max(900_000).optional(),
});

export const LlmBudgetDefaults = z.object({
  perExecutionLimitUsd: z.number().nonnegative().nullable().default(1),
  dailyLimitUsd: z.number().nonnegative().nullable().default(20),
  monthlyLimitUsd: z.number().nonnegative().nullable().default(200),
});

/** Embedding provider (ADR-016/029). Vectors are always EMBEDDING_DIMENSIONS long. */
export const EMBEDDING_DIMENSIONS = 1024;
export const EmbeddingConfig = z.object({
  kind: z.string().regex(/^[a-z0-9][a-z0-9-]*$/),
  model: z.string().min(1),
  credential: z
    .string()
    .regex(/^[a-z0-9][a-z0-9._-]{1,62}$/)
    .optional(),
  settings: z.record(z.string(), z.unknown()).default({}),
  eligibleClassifications: z.array(Classification).default(['PUBLIC']),
  batchSize: z.number().int().min(1).max(256).default(32),
});
export type EmbeddingConfig = z.infer<typeof EmbeddingConfig>;

export const LlmConfig = z
  .object({
    providers: z.record(z.string().regex(/^[a-z0-9][a-z0-9_-]*$/), LlmProviderConfig).default({}),
    profiles: z.partialRecord(ModelProfile, LlmProfileConfig).default({}),
    budgets: LlmBudgetDefaults.default({
      perExecutionLimitUsd: 1,
      dailyLimitUsd: 20,
      monthlyLimitUsd: 200,
    }),
    /** Optional embedder; without it knowledge search is lexical (FTS + trigram) only. */
    embedding: EmbeddingConfig.nullable().default(null),
    /** Days before encrypted prompt/response artifacts expire. */
    artifactRetentionDays: z.number().int().min(1).max(3650).default(180),
  })
  .superRefine((c, ctx) => {
    for (const [profile, p] of Object.entries(c.profiles)) {
      const provider = c.providers[p.provider];
      if (!provider) {
        ctx.addIssue({
          code: 'custom',
          message: `profile ${profile} references unknown provider ${p.provider}`,
        });
      } else if (!provider.pricing[p.model]) {
        ctx.addIssue({
          code: 'custom',
          message: `profile ${profile}: pricing for model ${p.model} is required (budgets are enforced on cost)`,
        });
      }
    }
  });
export type LlmConfig = z.infer<typeof LlmConfig>;

// ── Admin views ─────────────────────────────────────────────────────────────
export const LlmCallView = z.object({
  id: z.string().uuid(),
  stepKey: z.string().nullable(),
  promptKey: z.string().nullable(),
  promptVersion: z.string().nullable(),
  modelProfile: z.string(),
  provider: z.string(),
  model: z.string(),
  classification: z.string(),
  status: z.enum(['succeeded', 'failed', 'refused']),
  refusalReason: z.string().nullable(),
  attempt: z.number().int(),
  /** Why the call failed; validation issues carry schema paths and codes only (no content). */
  error: z
    .object({
      code: z.string(),
      message: z.string(),
      issues: z.array(z.object({ path: z.string(), code: z.string() })).max(10),
    })
    .nullable(),
  inputTokens: z.number().int(),
  outputTokens: z.number().int(),
  costUsd: z.number(),
  durationMs: z.number().int().nullable(),
  createdAt: z.iso.datetime(),
});
export type LlmCallView = z.infer<typeof LlmCallView>;

export const LlmSettingsView = z.object({
  providers: z.array(
    z.object({
      name: z.string(),
      kind: z.string(),
      external: z.boolean(),
      available: z.boolean(),
      credential: z.string().nullable(),
      credentialConfigured: z.boolean(),
      eligibleClassifications: z.array(z.string()),
    }),
  ),
  profiles: z.array(
    z.object({
      profile: z.string(),
      provider: z.string(),
      model: z.string(),
      maxOutputTokens: z.number().int(),
    }),
  ),
  budgets: z.object({
    perExecutionLimitUsd: z.number().nullable(),
    dailyLimitUsd: z.number().nullable(),
    monthlyLimitUsd: z.number().nullable(),
    source: z.enum(['organization', 'default']),
  }),
  spend: z.object({ todayUsd: z.number(), monthUsd: z.number() }),
});
export type LlmSettingsView = z.infer<typeof LlmSettingsView>;

export const LlmBudgetUpdate = z.object({
  perExecutionLimitUsd: z.number().nonnegative().max(10_000).nullable(),
  dailyLimitUsd: z.number().nonnegative().max(100_000).nullable(),
  monthlyLimitUsd: z.number().nonnegative().max(1_000_000).nullable(),
});
export type LlmBudgetUpdate = z.infer<typeof LlmBudgetUpdate>;
